import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
  Chip,
  Paper,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Devices as DevicesIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { apiService } from '../../services/api';
import { Device, Client } from '../../types';
import { useNavigate } from 'react-router-dom';
import { getDeviceDisplayName } from '../../utils/helpers';

interface QuickAssignFormData {
  imei: string;
  clientId: string;
  name: string;
  model: string;
  installationDate: Date | null;
}

const QuickAssign: React.FC = () => {
  const navigate = useNavigate();

  // Estado de búsqueda
  const [searchIMEI, setSearchIMEI] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [foundDevice, setFoundDevice] = useState<Device | null>(null);
  const [multipleDevices, setMultipleDevices] = useState<Device[]>([]);

  // Estado de formulario
  const [formData, setFormData] = useState<QuickAssignFormData>({
    imei: '',
    clientId: '',
    name: '',
    model: '',
    installationDate: new Date(),
  });

  // Estado de clientes
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Estado de asignación
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState(false);

  // Errores de validación
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    model?: string;
    clientId?: string;
    installationDate?: string;
  }>({});

  // Cargar clientes al montar el componente
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setClientsLoading(true);
      const response = await apiService.getClients(1, 1000);
      if (response.success && response.data) {
        setClients(response.data.clients || []);
      }
    } catch (error: any) {
      console.error('Error loading clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleSearchDevice = async () => {
    if (!searchIMEI.trim()) {
      setSearchError('Por favor ingrese un IMEI (mínimo 4 dígitos)');
      return;
    }

    if (searchIMEI.trim().length < 4) {
      setSearchError('Ingrese al menos 4 dígitos del IMEI');
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError(null);
      setFoundDevice(null);
      setMultipleDevices([]);
      setAssignSuccess(false);

      const response = await apiService.searchDeviceByIMEI(searchIMEI.trim());

      if (response.success && response.data) {
        // Verificar si hay múltiples resultados
        if (Array.isArray(response.data)) {
          // Múltiples dispositivos encontrados
          setMultipleDevices(response.data);
          setFoundDevice(null);
        } else {
          // Un solo dispositivo encontrado
          handleDeviceSelection(response.data);
        }
      }
    } catch (error: any) {
      console.error('Error searching device:', error);
      setSearchError(
        error.response?.data?.message ||
        'No se encontró ningún dispositivo'
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDeviceSelection = (device: Device) => {
    setFoundDevice(device);
    setMultipleDevices([]);

    // Pre-llenar el formulario
    const clientId = typeof device.clientId === 'object' && device.clientId !== null
      ? device.clientId._id
      : (device.clientId || '');

    setFormData({
      imei: device.imei,
      clientId: clientId,
      name: device.name || '',
      model: device.model || '',
      installationDate: device.installationDate
        ? new Date(device.installationDate)
        : new Date(),
    });

    // Si ya tiene cliente asignado, seleccionarlo
    if (device && device.clientId && typeof device.clientId === 'object' && device.clientId._id) {
      const clientIdStr = device.clientId._id;
      const client = clients.find(c => c._id === clientIdStr);
      setSelectedClient(client || null);
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'El nombre del dispositivo (chasis) es requerido';
    }

    if (!formData.model.trim()) {
      errors.model = 'El modelo es requerido';
    }

    if (!formData.clientId) {
      errors.clientId = 'Debe seleccionar un cliente';
    }

    if (!formData.installationDate) {
      errors.installationDate = 'La fecha de instalación es requerida';
    } else if (formData.installationDate > new Date()) {
      errors.installationDate = 'La fecha de instalación no puede ser en el futuro';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateExpirationDate = (): Date => {
    if (!formData.installationDate) return new Date();

    const expiration = new Date(formData.installationDate);
    expiration.setFullYear(expiration.getFullYear() + 1);
    return expiration;
  };

  const handleAssignDevice = async () => {
    // Validar formulario
    if (!validateForm()) {
      return;
    }

    try {
      setAssignLoading(true);
      setAssignError(null);

      const response = await apiService.quickAssignDevice({
        imei: formData.imei,
        clientId: formData.clientId,
        name: formData.name,
        model: formData.model,
        installationDate: formData.installationDate!.toISOString(),
      });

      if (response.success) {
        setAssignSuccess(true);

        // Limpiar formulario después de 3 segundos
        setTimeout(() => {
          handleReset();
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error assigning device:', error);
      setAssignError(
        error.response?.data?.message ||
        'Error al asignar el dispositivo. Por favor intente nuevamente.'
      );
    } finally {
      setAssignLoading(false);
    }
  };

  const handleReset = () => {
    setSearchIMEI('');
    setFoundDevice(null);
    setMultipleDevices([]);
    setFormData({
      imei: '',
      clientId: '',
      name: '',
      model: '',
      installationDate: new Date(),
    });
    setSelectedClient(null);
    setSearchError(null);
    setAssignError(null);
    setAssignSuccess(false);
    setValidationErrors({});
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchIMEI.trim()) {
      handleSearchDevice();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon fontSize="large" />
            Asignación Rápida de GPS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Busca un dispositivo por IMEI (completo o últimos 4 dígitos) y asígnalo rápidamente a un cliente
          </Typography>
        </Box>

        {/* Success Alert */}
        {assignSuccess && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
            onClose={() => setAssignSuccess(false)}
          >
            <strong>¡GPS asignado exitosamente!</strong>
            <br />
            El dispositivo ha sido asignado a {selectedClient?.name}.
          </Alert>
        )}

        {/* Búsqueda por IMEI */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon />
              Buscar Dispositivo por IMEI
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="IMEI (completo o últimos 4+ dígitos)"
                value={searchIMEI}
                onChange={(e) => setSearchIMEI(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ej: 9012 o 860123456789012"
                disabled={searchLoading || assignLoading}
                error={!!searchError}
                helperText={searchError}
              />
              <Button
                variant="contained"
                onClick={handleSearchDevice}
                disabled={searchLoading || assignLoading || !searchIMEI.trim()}
                startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ minWidth: 140 }}
              >
                {searchLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Múltiples Dispositivos Encontrados */}
        {multipleDevices.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DevicesIcon color="warning" />
                Se encontraron {multipleDevices.length} dispositivos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona el dispositivo correcto:
              </Typography>

              <Stack spacing={2}>
                {multipleDevices.map((device) => (
                  <Paper
                    key={device._id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'primary.main',
                      },
                    }}
                    onClick={() => handleDeviceSelection(device)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {getDeviceDisplayName(device)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          IMEI: {device.imei} • Estado: {device.status}
                          {typeof device.clientId === 'object' && device.clientId !== null && (
                            <> • Cliente: {device.clientId.name}</>
                          )}
                        </Typography>
                      </Box>
                      <Chip
                        label="Seleccionar"
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Dispositivo Encontrado */}
        {foundDevice && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DevicesIcon color="primary" />
                <Typography variant="h6">
                  Dispositivo Encontrado
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">IMEI</Typography>
                  <Typography variant="body1" fontWeight="bold">{foundDevice.imei}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Marca</Typography>
                  <Typography variant="body1" fontWeight="bold">{foundDevice.brand}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Estado</Typography>
                  <Box>
                    <Chip
                      label={foundDevice.status}
                      size="small"
                      color={foundDevice.status === 'active' ? 'success' : 'default'}
                    />
                  </Box>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Cliente Actual</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {typeof foundDevice.clientId === 'object' && foundDevice.clientId !== null
                      ? foundDevice.clientId.name
                      : 'Sin asignar'}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Formulario de Asignación */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Datos de Asignación
              </Typography>

              {assignError && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
                  {assignError}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => `${option.name} - ${option.email}`}
                    loading={clientsLoading}
                    value={selectedClient}
                    onChange={(_, newValue) => {
                      setSelectedClient(newValue);
                      setFormData({ ...formData, clientId: newValue?._id || '' });
                      setValidationErrors({ ...validationErrors, clientId: undefined });
                    }}
                    disabled={assignLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente *"
                        error={!!validationErrors.clientId}
                        helperText={validationErrors.clientId}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientsLoading ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email} • {option.clientType === 'dealer' ? 'Dealer' : 'Individual'}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Nombre del Dispositivo (Chasis) *"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setValidationErrors({ ...validationErrors, name: undefined });
                  }}
                  disabled={assignLoading}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name || 'Ej: Toyota Corolla ABC123'}
                  placeholder="Marca, modelo y placa del vehículo"
                />

                <TextField
                  fullWidth
                  label="Modelo del GPS *"
                  value={formData.model}
                  onChange={(e) => {
                    setFormData({ ...formData, model: e.target.value });
                    setValidationErrors({ ...validationErrors, model: undefined });
                  }}
                  disabled={assignLoading}
                  error={!!validationErrors.model}
                  helperText={validationErrors.model || 'Ej: GT06N, TK103, etc.'}
                  placeholder="Modelo del dispositivo GPS"
                />

                <DatePicker
                  label="Fecha de Instalación *"
                  value={formData.installationDate}
                  onChange={(newValue) => {
                    setFormData({ ...formData, installationDate: newValue });
                    setValidationErrors({ ...validationErrors, installationDate: undefined });
                  }}
                  disabled={assignLoading}
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!validationErrors.installationDate,
                      helperText: validationErrors.installationDate || 'Fecha de instalación del GPS',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Fecha de Expiración"
                  value={calculateExpirationDate().toLocaleDateString('es-DO')}
                  disabled
                  helperText="Se calcula automáticamente (1 año después de la instalación)"
                />
              </Box>

              {/* Información adicional */}
              <Alert severity="info" sx={{ mt: 3 }} icon={<CheckCircleIcon />}>
                <Typography variant="body2">
                  <strong>Importante:</strong> Al asignar el dispositivo se realizarán las siguientes acciones:
                </Typography>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>Se asignará el GPS al cliente seleccionado</li>
                  <li>Se actualizará el nombre y modelo del dispositivo</li>
                  <li>Se establecerá la fecha de expiración (1 año después de la instalación)</li>
                  <li>Se activará el dispositivo automáticamente</li>
                  <li>El cliente podrá ver el dispositivo en su app móvil</li>
                </ul>
              </Alert>

              {/* Botones */}
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleAssignDevice}
                  disabled={assignLoading}
                  startIcon={assignLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  fullWidth
                >
                  {assignLoading ? 'Asignando...' : 'Asignar y Activar GPS'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReset}
                  disabled={assignLoading}
                >
                  Cancelar
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default QuickAssign;
